import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DatePicker,
  Field,
  FieldLabel,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@beweegbaas/ui";
import { type CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import type { FormEvent } from "react";
import { useState } from "react";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { SystemNotificationDialog } from "#/modules/system-notifications/components/system-notification-dialog";
import {
  deleteAdminSystemNotification,
  getAdminSystemNotification,
  setAdminSystemNotification,
} from "#/modules/system-notifications/system-notification.functions";
import { systemNotificationTemplates } from "#/modules/system-notifications/templates";
import { formatDate } from "#/modules/util.helpers/format";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/admin/system-notification/",
)({
  loader: () => getAdminSystemNotification(),
  component: SystemNotificationAdmin,
  errorComponent: SystemNotificationError,
});

function SystemNotificationError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

const templateOptions = [...systemNotificationTemplates].map(([slug, template]) => ({
  slug,
  label: template.label,
}));

function templateLabel(slug: string) {
  return systemNotificationTemplates.get(slug)?.label ?? `${slug} (onbekend template)`;
}

/** `endsAt` is the start of the day after the chosen einddatum. */
function lastVisibleDay(endsAt: string) {
  return new Date(new Date(endsAt).getTime() - 1).toISOString();
}

function SystemNotificationAdmin() {
  const { active } = Route.useLoaderData();
  const router = useRouter();
  const setNotification = useServerFn(setAdminSystemNotification);
  const deleteNotification = useServerFn(deleteAdminSystemNotification);

  const [slug, setSlug] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<CalendarDate | null>(null);
  const [previewSlug, setPreviewSlug] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const handlePublish = async (e: FormEvent) => {
    e.preventDefault();
    if (!slug || !endDate) return;
    setIsPending(true);
    try {
      // The einddatum itself still counts: the notification ends at the start of the next day.
      const endsAt = endDate.add({ days: 1 }).toDate(getLocalTimeZone()).toISOString();
      await setNotification({ data: { slug, endsAt } });
    } catch {
      // toastErrorMiddleware already surfaced the error
      return;
    } finally {
      setIsPending(false);
    }
    toast.success("Melding gepubliceerd.");
    setSlug(null);
    setEndDate(null);
    await router.invalidate();
  };

  const handleEnd = async () => {
    try {
      await deleteNotification();
    } catch {
      return;
    }
    toast.success("Melding beëindigd.");
    await router.invalidate();
  };

  return (
    <Page>
      <Page.Header>
        <Page.Title>Melding</Page.Title>
      </Page.Header>
      <Page.Container className="flex max-w-2xl flex-col gap-6 pb-8">
        <Card>
          <CardHeader>
            <CardTitle>Actieve melding</CardTitle>
            <CardDescription>
              Elke gebruiker ziet de melding één keer, tot de einddatum. Er is maar één melding
              tegelijk actief.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {active ? (
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-col">
                  <span className="font-medium">{templateLabel(active.slug)}</span>
                  <span className="text-muted-foreground text-sm">
                    T/m {formatDate(lastVisibleDay(active.endsAt))}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setPreviewSlug(active.slug)}>
                    <IconMaterialSymbolsVisibilityOutline />
                    Bekijken
                  </Button>
                  <Button variant="destructive" onClick={() => void handleEnd()}>
                    Beëindigen
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Er is geen actieve melding.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Nieuwe melding</CardTitle>
            <CardDescription>
              Vervangt de actieve melding. Ook wie een eerdere melding met hetzelfde template al
              heeft gezien, krijgt hem opnieuw.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePublish} className="flex flex-col gap-4">
              <Field>
                <FieldLabel>Template</FieldLabel>
                <Select
                  value={slug ?? ""}
                  onValueChange={(value: string | null) => setSlug(value || null)}
                >
                  <SelectTrigger aria-label="Template" className="w-full">
                    <SelectValue>{slug ? templateLabel(slug) : "Kies een template"}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {templateOptions.map((option) => (
                        <SelectItem key={option.slug} value={option.slug}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
              <DatePicker
                label="Einddatum"
                aria-label="Einddatum"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                minValue={today(getLocalTimeZone())}
              />
              <div className="flex gap-2">
                <Button type="submit" disabled={!slug || !endDate} isPending={isPending}>
                  Publiceren
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!slug}
                  onClick={() => setPreviewSlug(slug)}
                >
                  <IconMaterialSymbolsVisibilityOutline />
                  Voorbeeld bekijken
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </Page.Container>
      {previewSlug ? (
        <SystemNotificationDialog slug={previewSlug} onClose={() => setPreviewSlug(null)} />
      ) : null}
    </Page>
  );
}
